import mixpanel, { type Config } from 'mixpanel-browser';
import { MIXPANEL_PROXY_HOST } from '../constants.js';
import type { AnalyticsIdentity, EventName, Events } from '../events.js';
import { EVENT_NAMES } from '../events.js';
import { isMixpanelExcludedEmail } from '../internalUsers.js';

const FLUSH_AT = 20;
const FLUSH_MS = 5000;
const QUEUE_CAP = 200;
const MAX_BATCH = 50;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const SCOPE_KEYS = ['brandName', 'accountIds'] as const;
const RELAYABLE = new Set<string>(EVENT_NAMES);
const CLIENT_ONLY_EVENTS = new Set<EventName>([
  'Auth - Login Started',
  'Auth - Login Failed',
  'Auth - Logout',
  'Invite - Accepted',
]);

type TrackArgs<T extends EventName> = Events[T] extends never ? [] : [Events[T]];

type InteractionProperties = Record<string, string | number | boolean>;

type SuperProperties = Record<string, string | number | boolean | Array<string | number>>;

type MixpanelInitConfig = Partial<Config> & { api_hosts?: { record?: string } };

export interface AnalyticsInitConfig {
  token: string | undefined;
  trackInternalUsers: boolean;
}

export interface QueuedEvent {
  name: EventName;
  properties: Record<string, unknown>;
  context: Record<string, unknown>;
  time: number;
}

export type RelayFn = (events: QueuedEvent[], options: { keepalive: boolean }) => Promise<unknown>;

function isRelayable(name: string): name is EventName {
  return RELAYABLE.has(name);
}

function browserContext(scope: SuperProperties): Record<string, unknown> {
  const params = new URLSearchParams(window.location.search);
  const utm = Object.fromEntries(
    UTM_KEYS.map((key) => [key, params.get(key)]).filter(([, value]) => value !== null),
  );
  return { ...scope, $current_url: window.location.href, $referrer: document.referrer, ...utm };
}

class Analytics {
  private initialized = false;
  private excluded = false;
  private trackInternalUsers = false;
  private queue: QueuedEvent[] = [];
  private scope: SuperProperties = {};
  private relay: RelayFn | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;

  init(config: AnalyticsInitConfig): void {
    if (this.initialized || config.token === undefined || config.token === '') return;
    const mixpanelConfig: MixpanelInitConfig = {
      api_host: MIXPANEL_PROXY_HOST,
      api_hosts: { record: MIXPANEL_PROXY_HOST },
      track_pageview: false,
      persistence: 'localStorage',
      record_sessions_percent: 0,
    };
    this.trackInternalUsers = config.trackInternalUsers;
    mixpanel.init(config.token, mixpanelConfig);
    this.initialized = true;
    window.addEventListener('pagehide', () => void this.flush(true));
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.flush(true);
    });
  }

  // Exposed so call sites that must fire analytics before identify() has run
  // (e.g. the brand-selection step of login, where the me.get query needed for
  // identify() is intentionally disabled) can still respect the exclusion.
  isExcludedEmail(email: string): boolean {
    return !this.trackInternalUsers && isMixpanelExcludedEmail(email);
  }

  identify(identity: AnalyticsIdentity): void {
    if (!this.initialized) return;
    this.excluded = this.isExcludedEmail(identity.email);
    if (this.excluded) {
      this.queue = [];
      return;
    }
    mixpanel.identify(String(identity.userId));
    mixpanel.people.set({
      $email: identity.email,
      $name: identity.name,
      organizationId: identity.organizationId,
      organizationName: identity.organizationName,
    });
    mixpanel.register({
      userId: identity.userId,
      userEmail: identity.email,
      userName: identity.name ?? '',
      organizationId: identity.organizationId,
      organizationName: identity.organizationName ?? '',
    });
    mixpanel.start_session_recording();
  }

  // Super properties attached to every subsequent event (e.g. selected client and
  // accounts), so all events carry who did it and in what context.
  register(properties: SuperProperties): void {
    if (!this.initialized || this.excluded) return;
    mixpanel.register(properties);
    for (const key of SCOPE_KEYS) {
      const value = properties[key];
      if (value !== undefined) this.scope[key] = value;
    }
  }

  // Registry events are fully typed; an arbitrary string is accepted for ad-hoc
  // component-level telemetry (e.g. the shared Button's trackEvent prop).
  track<T extends EventName>(name: T, ...properties: TrackArgs<T>): void;
  track(name: string, properties?: InteractionProperties): void;
  track(name: string, properties?: Record<string, unknown>): void {
    if (!this.initialized || this.excluded) return;
    if (!isRelayable(name)) return;
    if (CLIENT_ONLY_EVENTS.has(name)) {
      mixpanel.track(name, properties ?? {});
      return;
    }
    this.queue.push({
      name,
      properties: properties ?? {},
      context: browserContext(this.scope),
      time: Date.now(),
    });
    if (this.queue.length > QUEUE_CAP) this.queue.splice(0, this.queue.length - QUEUE_CAP);
    if (this.queue.length >= FLUSH_AT) void this.flush();
    else this.schedule();
  }

  trackPageView(path: string, routeId: string): void {
    this.track('Page Viewed', { path, routeId });
  }

  setRelay(relay: RelayFn | null): void {
    this.relay = relay;
  }

  async flush(keepalive = false): Promise<void> {
    if (this.relay === null || this.flushing || this.queue.length === 0) return;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const batch = this.queue.splice(0, MAX_BATCH);
    this.flushing = true;
    try {
      await this.relay(batch, { keepalive });
    } catch {
    } finally {
      this.flushing = false;
    }
    if (this.queue.length > 0) this.schedule();
  }

  private schedule(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, FLUSH_MS);
  }

  logout(): void {
    if (!this.initialized) return;
    void this.flush(true);
    mixpanel.stop_session_recording();
    mixpanel.reset();
    this.excluded = false;
    this.queue = [];
    this.scope = {};
    this.relay = null;
  }

  __resetForTests(): void {
    this.initialized = false;
    this.excluded = false;
    this.trackInternalUsers = false;
    this.queue = [];
    this.scope = {};
    this.relay = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.flushing = false;
  }
}

export const AnalyticsService = new Analytics();
